import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Modal,
  Typography,
  Space,
  message,
  Alert,
  Upload,
  Progress,
  Tag,
  Image,
  Tooltip,
  List,
  Grid,
  Row,
  Col,
  Empty,
  Spin,
  Input
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  UploadOutlined,
  AppstoreOutlined,
  CopyOutlined,
  DownloadOutlined,
  SearchOutlined,
  ClockCircleOutlined,
  CloudDownloadOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { applicationAPI } from '../utils/api';
import { Application, AppVersion, ApplicationUploadResult } from '../types';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;
const { Search } = Input;

const Applications: React.FC = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState<ApplicationUploadResult[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [searchText, setSearchText] = useState('');
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [versionModalVisible, setVersionModalVisible] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async (): Promise<Application[] | null> => {
    try {
      setLoading(true);
      setError(null);
      const data = await applicationAPI.getApplications();
      setApplications(data);
      if (selectedApp?.id) {
        const updatedSelected = data.find(app => app.id === selectedApp.id) || null;
        setSelectedApp(updatedSelected);
      }
      return data;
    } catch (err: any) {
      setError(err.message || '获取应用列表失败');
      console.error('获取应用列表失败:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('请先选择APK文件');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      setUploadResults([]);

      const files = fileList.map(f => f.originFileObj as File);
      
      const result = await applicationAPI.uploadApks(files, (percent) => {
        setUploadProgress(percent);
      });

      setUploadResults(result.results);
      
      const successCount = result.results.filter(r => r.success).length;
      const failCount = result.results.filter(r => !r.success).length;
      
      if (failCount === 0) {
        message.success(`上传成功：${successCount} 个应用`);
      } else {
        message.warning(`上传完成：成功 ${successCount} 个，失败 ${failCount} 个`);
      }

      // 刷新列表
      await fetchApplications();
      setFileList([]);
    } catch (err: any) {
      message.error(err.message || '上传失败');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await applicationAPI.deleteApplication(id);
      message.success('删除成功');
      fetchApplications();
    } catch (err: any) {
      message.error(err.message || '删除失败');
    }
  };

  const handleDeleteVersion = async (id: number, versionId: string) => {
    try {
      await applicationAPI.deleteVersion(id, versionId);
      message.success('版本删除成功');
      fetchApplications();
    } catch (err: any) {
      message.error(err.message || '删除版本失败');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('已复制到剪贴板');
  };

  // 获取应用的最新版本（按版本号数字排序，最大的在前面）
  const getSortedVersions = (versions: AppVersion[]): AppVersion[] => {
    if (!versions || versions.length === 0) return [];
    return [...versions].sort((a, b) => {
      return parseInt(b.versionCode) - parseInt(a.versionCode);
    });
  };

  // 获取应用的最新版本图标
  const getLatestIcon = (app: Application): string | undefined => {
    const sorted = getSortedVersions(app.versions);
    return sorted.length > 0 ? sorted[0].icon : undefined;
  };

  // 获取应用的最新版本号
  const getLatestVersionCode = (app: Application): string => {
    const sorted = getSortedVersions(app.versions);
    return sorted.length > 0 ? sorted[0].versionCode : '-';
  };

  // 获取应用的最新版本下载链接
  const getLatestDownloadUrl = (app: Application): string | undefined => {
    const sorted = getSortedVersions(app.versions);
    return sorted.length > 0 ? sorted[0].installUrl : undefined;
  };

  // 过滤应用列表
  const filteredApplications = applications.filter(app => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return app.applicationName.toLowerCase().includes(search) ||
           app.packageName.toLowerCase().includes(search);
  });

  // 打开版本管理弹窗
  const openVersionModal = (app: Application) => {
    setSelectedApp(app);
    setVersionModalVisible(true);
  };

  // 渲染应用卡片
  const renderAppCard = (app: Application) => {
    const icon = getLatestIcon(app);
    const latestVersion = getLatestVersionCode(app);
    const versionCount = app.versions?.length || 0;
    const downloadUrl = getLatestDownloadUrl(app);

    return (
      <Card
        hoverable
        style={{ 
          height: '100%',
          borderRadius: 12,
          overflow: 'hidden'
        }}
        bodyStyle={{ padding: isMobile ? 12 : 16 }}
        actions={[
          <Tooltip title="下载最新版本" key="download">
            <Button 
              type="text" 
              icon={<CloudDownloadOutlined />}
              disabled={!downloadUrl}
              onClick={() => downloadUrl && window.open(downloadUrl, '_blank')}
              style={{ color: downloadUrl ? '#1890ff' : undefined }}
            >
              {isMobile ? '' : '下载'}
            </Button>
          </Tooltip>,
          <Tooltip title="查看所有版本" key="versions">
            <Button 
              type="text" 
              icon={<ClockCircleOutlined />}
              onClick={() => openVersionModal(app)}
              style={{ color: '#52c41a' }}
            >
              {isMobile ? '' : `${versionCount}个版本`}
            </Button>
          </Tooltip>,
          <Tooltip title="删除应用" key="delete">
            <Button 
              type="text" 
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  title: '确定删除此应用？',
                  icon: <ExclamationCircleOutlined />,
                  content: (
                    <div>
                      <p>应用名称：<strong>{app.applicationName}</strong></p>
                      <p>包名：<code>{app.packageName}</code></p>
                      <p style={{ color: '#ff4d4f' }}>将删除该应用的所有 {versionCount} 个版本，此操作不可恢复！</p>
                    </div>
                  ),
                  okText: '确定删除',
                  okType: 'danger',
                  cancelText: '取消',
                  onOk: () => handleDelete(app.id)
                });
              }}
            >
              {isMobile ? '' : '删除'}
            </Button>
          </Tooltip>
        ]}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          {/* 应用图标 */}
          <div style={{ flexShrink: 0 }}>
            {icon ? (
              <Image
                src={icon}
                width={56}
                height={56}
                style={{ borderRadius: 12 }}
                preview={false}
                fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
              />
            ) : (
              <div style={{
                width: 56,
                height: 56,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <AppstoreOutlined style={{ fontSize: 28, color: '#fff' }} />
              </div>
            )}
          </div>
          
          {/* 应用信息 */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text strong style={{ fontSize: 15, display: 'block', marginBottom: 4 }} ellipsis>
              {app.applicationName}
            </Text>
            <Paragraph 
              type="secondary" 
              style={{ fontSize: 12, marginBottom: 8 }}
              copyable={{ text: app.packageName, tooltips: ['复制包名', '已复制'] }}
              ellipsis
            >
              {app.packageName}
            </Paragraph>
            <Space size={4}>
              <Tag color="blue" style={{ margin: 0 }}>{latestVersion}</Tag>
              <Tag color="green" style={{ margin: 0 }}>{versionCount}个版本</Tag>
            </Space>
          </div>
        </div>
      </Card>
    );
  };

  // 版本管理弹窗内容
  const renderVersionModal = () => {
    if (!selectedApp) return null;
    const sortedVersions = getSortedVersions(selectedApp.versions);

    return (
      <Modal
        title={
          <Space>
            {getLatestIcon(selectedApp) ? (
              <Image
                src={getLatestIcon(selectedApp)}
                width={24}
                height={24}
                style={{ borderRadius: 4 }}
                preview={false}
              />
            ) : (
              <AppstoreOutlined />
            )}
            <span>{selectedApp.applicationName} - 版本管理</span>
          </Space>
        }
        open={versionModalVisible}
        width={isMobile ? '95%' : 600}
        footer={null}
        onCancel={() => setVersionModalVisible(false)}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">包名：</Text>
          <Text code copyable>{selectedApp.packageName}</Text>
        </div>
        
        {sortedVersions.length === 0 ? (
          <Empty description="暂无版本" />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={sortedVersions}
            renderItem={(v: AppVersion, index: number) => (
              <List.Item
                actions={[
                  <Tooltip title="下载此版本" key="download">
                    <Button
                      type="primary"
                      ghost
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() => window.open(v.installUrl, '_blank')}
                    >
                      下载
                    </Button>
                  </Tooltip>,
                  <Tooltip title="复制下载链接" key="copy">
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() => copyToClipboard(v.installUrl)}
                    />
                  </Tooltip>,
                  <Tooltip title="删除此版本" key="delete">
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        Modal.confirm({
                          title: '确定删除此版本？',
                          content: `版本号：${v.versionCode}`,
                          okText: '删除',
                          okType: 'danger',
                          cancelText: '取消',
                          onOk: async () => {
                            let versionId: string | undefined = v.id;
                            if (!versionId) {
                              const latestApps = await fetchApplications();
                              const latestSelected = latestApps?.find(app => app.id === selectedApp.id);
                              const resolved = latestSelected?.versions.find(item =>
                                item.versionCode === v.versionCode &&
                                item.installUrl === v.installUrl &&
                                item.createdAt === v.createdAt
                              );
                              versionId = resolved?.id;
                            }

                            if (!versionId) {
                              message.error('版本ID缺失，已刷新列表但仍未找到，请重试');
                              return;
                            }

                            await handleDeleteVersion(selectedApp.id, versionId);
                            // 刷新选中的应用数据
                            const updated = applications.find(a => a.id === selectedApp.id);
                            if (updated) setSelectedApp(updated);
                          }
                        });
                      }}
                    />
                  </Tooltip>
                ]}
              >
                <List.Item.Meta
                  avatar={
                    v.icon ? (
                      <Image
                        src={v.icon}
                        width={40}
                        height={40}
                        style={{ borderRadius: 8 }}
                        preview={false}
                      />
                    ) : (
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        background: '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <AppstoreOutlined style={{ fontSize: 20, color: '#999' }} />
                      </div>
                    )
                  }
                  title={
                    <Space>
                      <Tag color={index === 0 ? 'green' : 'blue'}>
                        {index === 0 ? '最新 ' : ''}{v.versionCode}
                      </Tag>
                      {v.versionName && v.versionName !== v.versionCode && (
                        <Text type="secondary" style={{ fontSize: 12 }}>{v.versionName}</Text>
                      )}
                    </Space>
                  }
                  description={
                    <Space size={16}>
                      {v.createdAt && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          <ClockCircleOutlined style={{ marginRight: 4 }} />
                          {new Date(v.createdAt).toLocaleString('zh-CN')}
                        </Text>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Modal>
    );
  };

  return (
    <div>
      <div className="page-header">
        <Title level={2} style={{ margin: 0 }}>
          📦 应用管理
        </Title>
        <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: isMobile ? 12 : 14 }}>
          共 {applications.length} 个应用
        </p>
      </div>

      <Card
        className="content-card"
        extra={
          <Space wrap size={isMobile ? 'small' : 'middle'}>
            <Input
              placeholder="搜索应用名称或包名"
              allowClear
              style={{ width: isMobile ? 140 : 200 }}
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchApplications}
              loading={loading}
            >
              {isMobile ? '' : '刷新'}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setUploadModalVisible(true)}
            >
              {isMobile ? '上传' : '上传APK'}
            </Button>
          </Space>
        }
      >
        {/* 新手引导提示 */}
      {applications.length === 0 && !loading && !error && (
        <Alert
          message="开始使用"
          description={
            <div>
              <p>欢迎使用应用管理功能！您可以：</p>
              <ol style={{ paddingLeft: 20, marginBottom: 0 }}>
                <li>点击上方 <strong>「上传APK」</strong> 按钮上传您的Android应用</li>
                <li>系统会自动解析APK信息（应用名称、版本号、图标等）</li>
                <li>上传完成后，您可以管理应用的各个版本</li>
              </ol>
            </div>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16, borderRadius: 12 }}
        />
      )}

      {error && (
        <Alert
          message="加载失败"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: 16, borderRadius: 12 }}
          onClose={() => setError(null)}
        />
      )}

      {/* 应用卡片列表 */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">加载中...</Text>
          </div>
        </div>
      ) : filteredApplications.length === 0 ? (
        searchText ? (
          <Empty 
            description={`未找到包含 "${searchText}" 的应用`}
            style={{ padding: '60px 0' }}
          >
            <Button onClick={() => setSearchText('')}>清除搜索</Button>
          </Empty>
        ) : (
          <Empty 
            description="暂无应用"
            style={{ padding: '60px 0' }}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setUploadModalVisible(true)}>
              上传第一个APK
            </Button>
          </Empty>
        )
      ) : (
        <Row gutter={[16, 16]}>
          {filteredApplications.map(app => (
            <Col key={app.id} xs={24} sm={12} lg={8} xl={6}>
              {renderAppCard(app)}
            </Col>
          ))}
        </Row>
      )}
      </Card>

      {/* 上传APK弹窗 */}
      <Modal
        title="上传APK文件"
        open={uploadModalVisible}
        width={isMobile ? '95%' : 700}
        style={{ top: isMobile ? 20 : undefined }}
        onCancel={() => {
          if (!uploading) {
            setUploadModalVisible(false);
            setFileList([]);
            setUploadResults([]);
            setUploadProgress(0);
          }
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              if (!uploading) {
                setUploadModalVisible(false);
                setFileList([]);
                setUploadResults([]);
              }
            }}
            disabled={uploading}
          >
            取消
          </Button>,
          <Button
            key="upload"
            type="primary"
            icon={<UploadOutlined />}
            loading={uploading}
            onClick={handleUpload}
            disabled={fileList.length === 0}
          >
            开始上传
          </Button>,
        ]}
      >
        <Upload.Dragger
          multiple
          accept=".apk"
          fileList={fileList}
          onChange={({ fileList }) => setFileList(fileList)}
          beforeUpload={() => false}
          disabled={uploading}
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
          </p>
          <p className="ant-upload-text">点击或拖拽APK文件到此区域上传</p>
          <p className="ant-upload-hint">支持同时上传多个APK文件，系统将自动解析并上传到OSS</p>
        </Upload.Dragger>

        {uploading && (
          <div style={{ marginTop: 16 }}>
            <Progress percent={uploadProgress} status="active" />
            <Text type="secondary">正在上传和解析APK文件...</Text>
          </div>
        )}

        {uploadResults.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Title level={5}>上传结果</Title>
            <List
              size="small"
              dataSource={uploadResults}
              renderItem={(item) => (
                <List.Item>
                  <Space>
                    {item.success ? (
                      <Tag color="success">成功</Tag>
                    ) : (
                      <Tag color="error">失败</Tag>
                    )}
                    <Text strong>{item.applicationName}</Text>
                    <Text type="secondary">({item.packageName})</Text>
                    <Tag>{item.versionCode}</Tag>
                    {!item.success && item.error && (
                      <Text type="danger">{item.error}</Text>
                    )}
                  </Space>
                </List.Item>
              )}
            />
          </div>
        )}
      </Modal>

      {/* 版本管理弹窗 */}
      {renderVersionModal()}
    </div>
  );
};

export default Applications;
